import React from 'react';
import { PrinterIcon } from './icons/LucideIcons';
import usePrintClientStatus from '../hooks/usePrintClientStatus';
import { useAppearance } from '../contexts/AppearanceContext';

function formatDuration(totalSec) {
  if (!totalSec || totalSec < 60) return 'just now';
  const minutes = Math.floor(totalSec / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return remMin ? `${hours}h ${remMin}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

const PrintStationStatus = ({ isCollapsed }) => {
  const { connected, lastConnectedAt, disconnectedSince, offlineDurationSec } = usePrintClientStatus();
  const { appearance } = useAppearance();

  if (connected === null) return null; // loading / unknown
  // A shop that has never paired a print client has nothing to be offline from.
  if (!connected && !lastConnectedAt && !disconnectedSince) return null;

  const offline = !connected;
  const dotClass = offline ? 'bg-red-500' : 'bg-emerald-500';
  const containerClass = offline
    ? 'text-red-700 hover:bg-red-50'
    : 'text-gray-500 hover:bg-gray-50';
  const titleText = offline
    ? `Print station offline${offlineDurationSec ? ` for ${formatDuration(offlineDurationSec)}` : ''}. Make sure the front-desk computer is on and the print client is running.`
    : 'Print station online';

  // Forest: a footer row in the sidebar (the caller supplies the padding).
  // Expanded it is a status dot + label; the icon rail keeps the printer icon
  // so the dot still has something to belong to.
  if (appearance !== 'fable') {
    return (
      <div
        className={`flex items-center h-9 rounded-md text-[13px] transition-colors duration-[120ms] ${
          isCollapsed ? 'justify-center' : 'gap-2.5 px-2.5'
        } ${offline ? 'text-danger-fg hover:bg-danger-bg' : 'text-gray-500 hover:bg-floral-50'}`}
        title={titleText}
        aria-label={titleText}
      >
        {isCollapsed ? (
          <span className="flex-shrink-0 relative">
            <PrinterIcon size={17} strokeWidth={2.25} />
            <span
              className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${offline ? 'bg-danger' : 'bg-dot-ok'}`}
              aria-hidden="true"
            />
          </span>
        ) : (
          <>
            <span
              className={`flex-shrink-0 w-2 h-2 mx-[5px] rounded-full ${offline ? 'bg-danger' : 'bg-dot-ok'}`}
              aria-hidden="true"
            />
            <span className="flex-1 whitespace-nowrap overflow-hidden">
              {offline ? 'Printer offline' : 'Printer online'}
            </span>
            {offline && (
              <span className="text-gray-500 tabular whitespace-nowrap">
                {formatDuration(offlineDurationSec)}
              </span>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 flex-shrink-0">
      <div
        className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${containerClass} ${isCollapsed ? 'justify-center' : ''}`}
        title={titleText}
        aria-label={titleText}
      >
        <span className="flex-shrink-0 relative">
          <PrinterIcon size={17} strokeWidth={2.25} />
          <span
            className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-sidebar ${dotClass}`}
            aria-hidden="true"
          />
        </span>
        {!isCollapsed && (
          <>
            <span className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden">
              {offline ? 'Printer offline' : 'Printer online'}
            </span>
            {offline && (
              <span className="ml-auto text-xs text-red-700/80 whitespace-nowrap">
                {formatDuration(offlineDurationSec)}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PrintStationStatus;
